// public/js/components/custom-select.js

const CLASSES = {
  valid: ['bg-teal-50', 'border-teal-600', 'focus:ring-teal-700'],
  invalid: ['bg-red-50', 'border-red-600', 'focus:ring-red-600'],
  neutral: ['bg-white', 'border-slate-600', 'focus:ring-teal-700']
};

export default function CustomSelect(rootEl) {
  const toggleBtn = rootEl.querySelector('[data-action="toggle"]');
  const input = rootEl.querySelector('[data-ref="input"]');
  const menu = rootEl.querySelector('[data-ref="menu"]');
  const searchInput = rootEl.querySelector('[data-ref="search"]');
  const list = rootEl.querySelector('[data-ref="list"]');
  const display = rootEl.querySelector('[data-ref="display"]');
  const errorDiv = rootEl.querySelector('[data-ref="error"]');
  const options = Array.from(rootEl.querySelectorAll('[data-value]'));

  if (!toggleBtn || !input || !menu || !display) return;

  const menuId = menu.id || `select-menu-${Math.random().toString(36).substring(2, 11)}`;
  menu.id = menuId;
  toggleBtn.setAttribute('aria-controls', menuId);
  toggleBtn.setAttribute('aria-haspopup', 'listbox');

  let isOpen = false;
  let hasInteracted = false;

  const setVisualState = (state, message = '') => {
    if (errorDiv) {
      errorDiv.textContent = message;
      if (message) errorDiv.classList.remove('hidden');
      else errorDiv.classList.add('hidden');
    }

    toggleBtn.classList.remove(
      ...CLASSES.valid, 
      ...CLASSES.invalid, 
      ...CLASSES.neutral
    );

    switch (state) {
      case 'valid':
        toggleBtn.classList.add(...CLASSES.valid);
        break;
      case 'invalid':
        toggleBtn.classList.add(...CLASSES.invalid);
        break;
      default:
        toggleBtn.classList.add(...CLASSES.neutral);
    }
  };

  const validate = () => {
    const isRequired = input.hasAttribute('required');
    const value = input.value;

    if (isRequired && !value) {
      setVisualState('invalid', 'Please select an option.');
      return false;
    } 
    
    if (value) {
      setVisualState('valid');
    } else {
      setVisualState('neutral');
    }
    return true;
  };

  rootEl.validate = validate;

  Object.defineProperty(rootEl, 'value', {
    get() {
      return input.value;
    },
    set(newVal) {
      if (newVal === null || newVal === undefined || newVal === '') {
        display.textContent = rootEl.dataset.placeholder || 'Select an option';
        display.classList.add('text-slate-500');
        display.classList.remove('text-slate-800');
        toggleBtn.dataset.hasValue = 'false';
        input.value = '';
        options.forEach(o => o.setAttribute('aria-selected', 'false'));
        toggleBtn.setAttribute('aria-activedescendant', '');
        hasInteracted = false;
        setVisualState('neutral');
        return;
      }
      const targetOption = options.find((opt) => opt.dataset.value === String(newVal));
      if (targetOption) {
        updateVisualState(targetOption);
        return;
      }
    }
  });

  const updateVisualState = (option) => {
    const value = option.dataset.value;
    const label = option.dataset.text;

    display.textContent = label;
    display.classList.remove('text-slate-500');
    display.classList.add('text-slate-800');
    toggleBtn.dataset.hasValue = 'true';
    if (input.value !== value) {
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    options.forEach((opt) => opt.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');
    toggleBtn.setAttribute('aria-activedescendant', option.id || '');
    
    hasInteracted = true;
    validate();
  };

  const close = () => {
    menu.classList.add('opacity-0', 'scale-95');
    toggleBtn.setAttribute('aria-expanded', 'false');
    isOpen = false;

    if (searchInput) {
      searchInput.value = '';
      options.forEach(opt => opt.classList.remove('hidden'));
    }

    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('keydown', handleGlobalKeydown);
    window.removeEventListener('resize', positionMenu);
    window.removeEventListener('scroll', positionMenu, true);

    setTimeout(() => {
      if (!isOpen) {
        menu.classList.add('hidden');
        menu.style.position = '';
        menu.style.top = '';
        menu.style.left = '';
        menu.style.width = '';
        menu.style.bottom = '';
        menu.style.zIndex = '';
        menu.style.flexDirection = '';
        
        if (list) list.style.maxHeight = '';
      }
    }, 100);
    
    if (hasInteracted) validate();
  };

  const positionMenu = () => {
    const rect = toggleBtn.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const nav = document.querySelector('nav.fixed.bottom-0');
    const navHeight = nav ? nav.offsetHeight : 0;

    const spaceAbove = rect.top; 
    const spaceBelow = viewportHeight - rect.bottom - navHeight;
    const estimatedHeight = 280;

    let openUpwards = false;
    
    if (spaceBelow >= estimatedHeight) {
        openUpwards = false;
    } else if (spaceAbove >= estimatedHeight) {
        openUpwards = true;
    } else {
        openUpwards = spaceAbove > spaceBelow;
    }

    let left = rect.left;
    let width = rect.width;
    if (left + width > viewportWidth) {
      left = viewportWidth - width - 10;
    }

    menu.style.left = `${left}px`;
    menu.style.width = `${width}px`;
    menu.style.position = 'fixed';
    menu.style.zIndex = '9999';
    menu.style.flexDirection = 'column';
    
    if (list) list.style.maxHeight = '';

    const searchHeight = 50;
    const safetyMargin = 10;

    if (openUpwards) {
      menu.style.bottom = `${viewportHeight - rect.top + 4}px`;
      menu.style.top = 'auto';
      
      if (list) {
         const maxH = spaceAbove - searchHeight - safetyMargin;
         list.style.maxHeight = `${Math.min(240, maxH)}px`;
      }
    } else {
      menu.style.top = `${rect.bottom + 4}px`;
      menu.style.bottom = 'auto';
      
      if (list) {
         const maxH = spaceBelow - searchHeight - safetyMargin;
         list.style.maxHeight = `${Math.min(240, maxH)}px`;
      }
    }
  };

  const open = () => {
    document.dispatchEvent(new CustomEvent('close-all-menus'));
    menu.classList.remove('hidden');
    positionMenu(); 
    
    requestAnimationFrame(() => {
      menu.classList.remove('opacity-0', 'scale-95');
    });

    toggleBtn.setAttribute('aria-expanded', 'true');
    isOpen = true;
    hasInteracted = true;

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleGlobalKeydown);
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);

    if (searchInput) {
      searchInput.focus();
    } else {
      const selected = options.find((opt) => opt.getAttribute('aria-selected') === 'true');
      (selected ?? options[0])?.focus();
    }
  };

  const toggle = () => {
    if (isOpen) close();
    else open();
  };

  const handleOutsideClick = (e) => {
    if (!rootEl.contains(e.target) && !menu.contains(e.target)) close();
  };

  const handleGlobalKeydown = (e) => {
    if (e.key === 'Escape') {
      close();
      toggleBtn.focus();
    }
    if (e.key === 'Tab') {
      close();
    }
  };

  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      const term = e.target.value.toLowerCase();
      options.forEach(opt => {
        const text = (opt.dataset.text || '').toLowerCase();
        if (text.includes(term)) {
          opt.classList.remove('hidden');
        } else {
          opt.classList.add('hidden');
        }
      });
    });

    searchInput.addEventListener('keydown', (e) => {
      // Allow navigation from search input to list
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const firstVisible = options.find(opt => !opt.classList.contains('hidden'));
        if (firstVisible) firstVisible.focus();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        // Select first visible if exactly one matches? Or just first visible.
        const firstVisible = options.find(opt => !opt.classList.contains('hidden'));
        if (firstVisible) {
          updateVisualState(firstVisible);
          close();
          toggleBtn.focus();
        }
      }
    });
  }

  const initialValue = input.value;
  if (initialValue) {
    const selectedOption = options.find((opt) => opt.dataset.value === initialValue);
    if (selectedOption) {
      toggleBtn.setAttribute('aria-activedescendant', selectedOption.id || '');
    }
  }

  toggleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    toggle();
  });

  options.forEach((option) => {
    if (!option.id) option.id = `opt-${Math.random().toString(36).substring(2, 11)}`;

    option.addEventListener('click', (e) => {
      e.stopPropagation();
      updateVisualState(option);
      close();
      toggleBtn.focus();
    });
    
    // Keydown on Options
    option.addEventListener('keydown', (e) => {
      // We need to handle navigation skipping hidden items
      const visibleOptions = options.filter(opt => !opt.classList.contains('hidden'));
      const idx = visibleOptions.indexOf(option);
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (idx < visibleOptions.length - 1) visibleOptions[idx + 1].focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (idx > 0) visibleOptions[idx - 1].focus();
          else if (searchInput) searchInput.focus(); // Go back to search
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          updateVisualState(option);
          close();
          toggleBtn.focus();
          break;
      }
    });
  });

  rootEl.addEventListener('keydown', (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    }
  });

  document.addEventListener('close-all-menus', () => {
    if (isOpen) close();
  });
}